/**
 * Dynamic Color Extraction System
 * Automatically extracts dominant colors from images and applies them to backgrounds and text elements
 */
(function dominantColors() {
    // Check if ColorThief is available
    if (typeof ColorThief === 'undefined') {
        console.warn('ColorThief library not loaded');
        return;
    }

    const colorThief = new ColorThief();
    const cacheKey = 'dominantColors';

    // Processing state tracking
    let isProcessing = false;
    let processingTimeout = null;

    function loadColorCache() {
        const cached = localStorage.getItem(cacheKey);
        return cached ? JSON.parse(cached) : {};
    }

    let colorCache = loadColorCache();

    function saveColorCache() {
        localStorage.setItem(cacheKey, JSON.stringify(colorCache));
    }

    function getCachedColor(imageUrl) {
        return colorCache[imageUrl];
    }

    function setCachedColor(imageUrl, color) {
        colorCache[imageUrl] = color;
        saveColorCache();
    }

    function getSmallestImageUrl(img) {
        if (img.srcset) {
            const sources = img.srcset.split(',')
                .map(src => {
                    const [url, width] = src.trim().split(' ');
                    return { url, width: parseInt(width) };
                })
                .sort((a, b) => a.width - b.width);

            return sources[0].url;
        }
        return img.src;
    }

    function getDominantColor(imageUrl) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = function() {
                try {
                    const color = colorThief.getColor(img);
                    resolve(color);
                } catch (error) {
                    reject(error);
                }
            };
            img.onerror = () => reject(new Error('Image failed to load'));
            img.src = imageUrl;
        });
    }

    function rgbToString(r, g, b) {
        return `rgb(${r}, ${g}, ${b})`;
    }

    function getContrastYIQ(r, g, b) {
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? 'text-black' : 'text-white';
    }

    function getColorFromClasses(element) {
        const classes = element.className.split(' ');
        const colorClass = classes.find(cls => cls.startsWith('tag-hash-color-'));
        if (colorClass) {
            const hexCode = colorClass.replace('tag-hash-color-', '');
            return hexToRgb(hexCode);
        }
        return null;
    }

    function hexToRgb(hex) {
        hex = hex.replace(/^#/, '');
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return [r, g, b];
    }

    function parseColorValue(colorValue) {
        if (!colorValue || colorValue === 'transparent' || colorValue === 'inherit') {
            return null;
        }

        // Remove any whitespace
        colorValue = colorValue.trim();

        // Handle hex colors
        if (colorValue.startsWith('#')) {
            return hexToRgb(colorValue);
        }

        // Handle rgb/rgba colors
        const rgbMatch = colorValue.match(/rgba?\(([^)]+)\)/);
        if (rgbMatch) {
            const values = rgbMatch[1].split(',').map(v => parseInt(v.trim()));
            return [values[0], values[1], values[2]];
        }

        // Handle hsl/hsla colors
        const hslMatch = colorValue.match(/hsla?\(([^)]+)\)/);
        if (hslMatch) {
            const values = hslMatch[1].split(',').map(v => parseFloat(v.trim()));
            return hslToRgb(values[0], values[1], values[2]);
        }

        return null;
    }

    function hslToRgb(h, s, l) {
        h = h / 360;
        s = s / 100;
        l = l / 100;

        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };

        if (s === 0) {
            const gray = Math.round(l * 255);
            return [gray, gray, gray];
        }

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        const r = Math.round(hue2rgb(p, q, h + 1/3) * 255);
        const g = Math.round(hue2rgb(p, q, h) * 255);
        const b = Math.round(hue2rgb(p, q, h - 1/3) * 255);

        return [r, g, b];
    }

    function applyColorToElement([r, g, b], targetElement, isHero = false) {
        const backgroundColor = rgbToString(r, g, b);
        const textColorClass = getContrastYIQ(r, g, b);

        if (isHero) {
            // Apply background color to hero section
            targetElement.style.backgroundColor = backgroundColor;

            // Update theme-color meta tags
            const themeColorMetaTags = document.querySelectorAll('meta[name="theme-color"]');
            themeColorMetaTags.forEach(meta => {
                meta.setAttribute('content', backgroundColor);
            });

            // Apply text color to header
            // const headerElement = document.querySelector('.header');
            // if (headerElement) {
            //     headerElement.classList.remove('text-black', 'text-white');
            //     headerElement.classList.add(textColorClass);
            // }

            // Apply text color class to hero wrapper
            targetElement.classList.remove('text-black', 'text-white');
            targetElement.classList.add(textColorClass);
        } else {
            targetElement.style.backgroundColor = backgroundColor;

            // Apply text color class to the post card element
            targetElement.classList.remove('text-black', 'text-white');
            targetElement.classList.add(textColorClass);
        }
    }

    function processImage(image, targetElement, isHero = false) {
        if (!image || !targetElement) return;

        const imageUrl = getSmallestImageUrl(image);
        const cachedColor = getCachedColor(imageUrl);

        if (cachedColor) {
            applyColorToElement(cachedColor, targetElement, isHero);
        } else {
            getDominantColor(imageUrl)
                .then(color => {
                    setCachedColor(imageUrl, color);
                    applyColorToElement(color, targetElement, isHero);
                })
                .catch(() => {/* Silently handle error */});
        }
    }

    function processPostCards() {
        // Target both post card types used in your theme
        const postCards = document.querySelectorAll('article.post-card');

        postCards.forEach((card) => {
            const colorMode = card.getAttribute('data-dominant-color');

            // Skip processing entirely if disabled
            if (colorMode === 'false') return;

            const customColor = getColorFromClasses(card);
            const image = card.querySelector('.post-img, .u-object-fit');

            // Handle static mode (custom colors only, skip image processing)
            if (colorMode === 'static') {
                if (customColor) {
                    // Convert RGB array to hex color
                    const [r, g, b] = customColor;
                    const hex = '#' + [r, g, b].map(x => {
                        const hex = x.toString(16);
                        return hex.length === 1 ? '0' + hex : hex;
                    }).join('');

                    card.setAttribute('data-accent-color', hex);
                    applyColorToElement(customColor, card, false);
                }
                return;
            }

            // Default behavior (colorMode === 'true' or undefined)
            if (customColor) {
                // Convert RGB array to hex color
                const [r, g, b] = customColor;
                const hex = '#' + [r, g, b].map(x => {
                    const hex = x.toString(16);
                    return hex.length === 1 ? '0' + hex : hex;
                }).join('');

                card.setAttribute('data-accent-color', hex);
                applyColorToElement(customColor, card, false);
            } else if (image) {
                processImage(image, card);
            }
        });
    }

    function processHero() {
        const heroWrapper = document.querySelector('.hero-wrapper');
        const heroImage = heroWrapper?.querySelector('.single-hero-image');
        const bodyElement = document.body;

        if (heroWrapper) {
            // Check for custom background color from Ghost settings
            const isHomepage = document.body.classList.contains('home-template');

            if (isHomepage) {
                const customBgColor = heroWrapper.getAttribute('data-background-color');
                if (customBgColor) {
                    const rgbColor = hexToRgb(customBgColor);
                    if (rgbColor) {
                        applyColorToElement(rgbColor, heroWrapper, true);
                        return;
                    }
                }
            }

            const customColor = getColorFromClasses(bodyElement);
            if (customColor) {
                applyColorToElement(customColor, heroWrapper, true);
            } else if (heroImage) {
                processImage(heroImage, heroWrapper, true);
            }
        }
    }

    function processHeaderCards() {
        // Support Ghost's content cards
        const headerCards = document.querySelectorAll(
            '.kg-header-card, .kg-signup-card, .kg-product-card'
        );

        headerCards.forEach((card) => {
            const picture = card.querySelector('picture img, img');
            if (!picture) return;

            const contentElement = card.querySelector('.kg-header-card-text, .kg-signup-card-content, .kg-product-card-content');
            if (!contentElement) return;

            const imageUrl = getSmallestImageUrl(picture);
            const cachedColor = getCachedColor(imageUrl);

            if (cachedColor) {
                applyHeaderCardClasses(cachedColor, card);
            } else {
                getDominantColor(imageUrl)
                    .then(color => {
                        setCachedColor(imageUrl, color);
                        applyHeaderCardClasses(color, card);
                    })
                    .catch(() => {/* Silently handle error */});
            }
        });
    }

    function processFooter() {
        const footerInnerMain = document.querySelector('.footer-inner-main');
        if (!footerInnerMain) return;

        // Get the computed brand color from CSS variables
        const computedStyle = getComputedStyle(document.documentElement);
        const brandColor = computedStyle.getPropertyValue('--brand-color');

        if (!brandColor) return;

        // Parse the brand color to RGB
        const rgbColor = parseColorValue(brandColor);
        if (!rgbColor) return;

        // Calculate contrast and apply appropriate text color class
        const textColorClass = getContrastYIQ(rgbColor[0], rgbColor[1], rgbColor[2]);

        // Remove existing contrast classes and add the new one
        footerInnerMain.classList.remove('text-black', 'text-white');
        footerInnerMain.classList.add(textColorClass);
    }

    function applyHeaderCardClasses([r, g, b], card) {
        const textColorClass = getContrastYIQ(r, g, b);
        card.classList.remove('text-black', 'text-white');
        card.classList.add(textColorClass);
    }

    function processPage() {
        if (isProcessing) return;
        isProcessing = true;

        try {
            processHero();
            processPostCards();
            processHeaderCards();
            processFooter();
        } finally {
            // Reset processing state after a short delay to allow DOM updates to complete
            setTimeout(() => {
                isProcessing = false;
            }, 100);
        }
    }

    function ensureProcessing() {
        if (isProcessing) return;
        processPage();

        // Only run once more after a delay, not continuously
        if (processingTimeout) {
            clearTimeout(processingTimeout);
        }
        processingTimeout = setTimeout(() => {
            processPage();
            processingTimeout = null;
        }, 1000);
    }

    // Initialize when ColorThief is available
    function initializeWhenReady() {
        if (typeof ColorThief !== 'undefined') {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', ensureProcessing);
            } else {
                ensureProcessing();
            }

            window.addEventListener('load', ensureProcessing);

            // Monitor for dynamic content, but ignore our own changes
            const observer = new MutationObserver((mutations) => {
                if (isProcessing) return; // Don't trigger during our own processing

                let shouldProcess = false;
                for (let mutation of mutations) {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        // Check if added nodes contain images or post cards
                        const addedNodes = Array.from(mutation.addedNodes);
                        const hasRelevantContent = addedNodes.some(node =>
                            node.nodeType === 1 && (
                                node.querySelector && (
                                    node.querySelector('img, .post-card, .hero-wrapper') ||
                                    node.matches && node.matches('img, .post-card, .hero-wrapper')
                                )
                            )
                        );

                        if (hasRelevantContent) {
                            shouldProcess = true;
                            break;
                        }
                    }
                }

                if (shouldProcess) {
                    // Debounce the processing to avoid multiple rapid calls
                    if (processingTimeout) {
                        clearTimeout(processingTimeout);
                    }
                    processingTimeout = setTimeout(() => {
                        processPage();
                        processingTimeout = null;
                    }, 500);
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            // Listen for custom events
            document.addEventListener('heroColorReset', () => {
                processHero();
            });
        } else {
            // Retry if ColorThief isn't loaded yet
            setTimeout(initializeWhenReady, 100);
        }
    }

    initializeWhenReady();
})();